import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Box, Typography, Stack, CircularProgress, Alert, AlertTitle, Button, IconButton,
  Chip, TextField, Autocomplete, Select, MenuItem, FormControl, InputLabel,
  LinearProgress, Collapse,
} from '@mui/material';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import ErrorOutlineRounded from '@mui/icons-material/ErrorOutlineRounded';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import QrCodeScannerRounded from '@mui/icons-material/QrCodeScannerRounded';
import PaletteRounded from '@mui/icons-material/PaletteRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import LocalShippingRounded from '@mui/icons-material/LocalShippingRounded';

import {
  kitInkTrolley, listInkLocations, listInkProducts, getJobGroups, getInkDemandForJobs,
  listInkUnits, listPressConfig, getInkSticker,
  InkTrolleyRun, InkTrolleyKitPayload, InkLocation, InkProduct, JobGroup, jobGroupLabel, InkUnitRow,
} from '../productionApi';
import { productionKeys } from '../queryKeys';
import { TOKENS } from '../board/ganttConfig';
import { fmtKg, fmtDate, errMsg, fifoHints, tubsToCover, FifoHint } from './kitFormat';
import { resolveScan } from './kitScan';
import { useInkInvalidate } from './useInkInvalidate';

const up = (s: string) => s.toUpperCase();

/** What the scan bar is currently saying. Persists until the next scan. */
type Feedback =
  | { tone: 'ok'; title: string; detail?: string }
  | { tone: 'warn'; title: string; detail?: string }
  | { tone: 'bad'; title: string; detail?: string }
  | null;

/** One row of the pick list: an ink the run needs, and the tubs scanned for it. */
interface PickRow {
  inkKey: string;
  /** Ink product, when the catalogue holds it. Null = assigned to something unknown. */
  product: InkProduct | null;
  /** Display name — the ink's description, or the separation name when unassigned. */
  name: string;
  hex: string | null;
  /** Press unit this colour runs on, when the job's sticker resolved. */
  unit: number | null;
  demandKg: number;
  /** Whole tubs that cover the demand; null when the tub size isn't known. */
  targetTubs: number | null;
  tubKg: number | null;
  fifo: FifoHint | undefined;
  scanned: InkUnitRow[];
}

export default function KitTrolleyPage() {
  const navigate = useNavigate();
  const invalidate = useInkInvalidate();

  // ---- what we're kitting -------------------------------------------------
  const [group, setGroup] = useState<JobGroup | null>(null);
  const [manualJobs, setManualJobs] = useState(false);
  const [jobs, setJobs] = useState<string[]>([]);
  const [jobDraft, setJobDraft] = useState('');
  const [trolleyCode, setTrolleyCode] = useState('');
  const [pressCode, setPressCode] = useState('');
  const [editingSetup, setEditingSetup] = useState(true);

  // ---- the pick ------------------------------------------------------------
  const [scanInput, setScanInput] = useState('');
  const [scannedCodes, setScannedCodes] = useState<string[]>([]);
  const [feedback, setFeedback] = useState<Feedback>(null);
  const [result, setResult] = useState<{ run: InkTrolleyRun; warnings: string[] } | null>(null);
  const scanRef = useRef<HTMLInputElement | null>(null);

  // ---- data ---------------------------------------------------------------
  const locationsQ = useQuery({
    queryKey: productionKeys.inkLocations(), queryFn: listInkLocations, staleTime: 60_000,
  });
  const trolleys = useMemo(
    () => (locationsQ.data ?? []).filter((l) => l.location_type === 'trolley' && l.active),
    [locationsQ.data],
  );

  const groupsQ = useQuery({
    queryKey: productionKeys.jobGroups({ limit: 200 }),
    queryFn: () => getJobGroups({ limit: 200 }),
    staleTime: 60_000,
    enabled: !manualJobs,
  });

  const pressConfigQ = useQuery({
    queryKey: productionKeys.pressConfig(), queryFn: listPressConfig, staleTime: 5 * 60_000,
  });
  const pressCodes = useMemo(() => {
    const codes = (pressConfigQ.data ?? []).map((p) => p.press_code);
    if (pressCode && !codes.includes(pressCode)) codes.push(pressCode);
    return codes;
  }, [pressConfigQ.data, pressCode]);

  const productsQ = useQuery({
    queryKey: productionKeys.inkProducts({ materialClass: 'all' }),
    queryFn: () => listInkProducts({ materialClass: 'all' }),
    staleTime: 5 * 60_000,
  });

  // Every sealed tub in the store: the scan lookup AND the FIFO hint per ink.
  const storeUnitsQ = useQuery({
    queryKey: ['inkStoreSealedUnits'],
    queryFn: () => listInkUnits({ location: 'STORE', status: 'sealed' }),
    staleTime: 15_000,
  });

  const activeJobs = group?.job_numbers ?? jobs;
  const demandQ = useQuery({
    queryKey: productionKeys.inkDemandForJobs(activeJobs),
    queryFn: () => getInkDemandForJobs(activeJobs),
    enabled: activeJobs.length > 0,
    staleTime: 60_000,
  });

  // The press unit each colour runs on, so the pick list reads in the same
  // order as the sticker already stuck to the Job Bag. One job's sticker is
  // enough — a gang run is one forme, so every job on it shares the layout.
  // Best-effort: it pulls Cloudflow, so a miss just costs the unit badges.
  const stickerQ = useQuery({
    queryKey: ['inkKitSticker', activeJobs[0] ?? ''],
    queryFn: () => getInkSticker(activeJobs[0]),
    enabled: activeJobs.length > 0,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const unitByInk = useMemo(() => {
    const m = new Map<string, number>();
    (stickerQ.data?.units ?? []).forEach((u) => {
      if (u.ink_key) m.set(u.ink_key, u.unit);
    });
    return m;
  }, [stickerQ.data]);

  const productByKey = useMemo(() => {
    const m = new Map<string, InkProduct>();
    (productsQ.data ?? []).forEach((p) => m.set(p.ink_key, p));
    return m;
  }, [productsQ.data]);

  const unitByCode = useMemo(() => {
    const m = new Map<string, InkUnitRow>();
    (storeUnitsQ.data ?? []).forEach((u) => m.set(u.unit_code, u));
    return m;
  }, [storeUnitsQ.data]);

  const hints = useMemo(() => fifoHints(storeUnitsQ.data ?? []), [storeUnitsQ.data]);
  const scannedSet = useMemo(() => new Set(scannedCodes), [scannedCodes]);

  // ---- the pick list -------------------------------------------------------
  const demand = demandQ.data;

  /** Separations with weight but no ink product behind them — cannot be picked at all. */
  const unassigned = useMemo(
    () => (demand?.inks ?? []).filter((r) => !r.assigned || !r.ink_key),
    [demand],
  );

  const rows = useMemo<PickRow[]>(() => {
    const scannedByInk = new Map<string, InkUnitRow[]>();
    scannedCodes.forEach((c) => {
      const u = unitByCode.get(c);
      if (!u) return;
      const a = scannedByInk.get(u.ink_key) ?? [];
      a.push(u);
      scannedByInk.set(u.ink_key, a);
    });

    const out: PickRow[] = [];
    const seen = new Set<string>();
    (demand?.inks ?? []).forEach((r) => {
      if (!r.assigned || !r.ink_key) return;
      const product = productByKey.get(r.ink_key) ?? null;
      const fifo = hints.get(r.ink_key);
      // The tub size comes from a REAL tub in the store first — pack_size_kg is
      // a purchasing conversion and can drift from what was actually received.
      const tubKg = fifo?.nominalKg ?? product?.pack_size_kg ?? null;
      seen.add(r.ink_key);
      out.push({
        inkKey: r.ink_key,
        product,
        name: r.name,
        hex: r.hex_colour ?? product?.hex_colour ?? null,
        unit: unitByInk.get(r.ink_key) ?? null,
        demandKg: r.total_kg,
        targetTubs: tubsToCover(r.total_kg, tubKg),
        tubKg,
        fifo,
        scanned: scannedByInk.get(r.ink_key) ?? [],
      });
    });

    // Anything scanned that the run's demand never asked for. Never dropped —
    // the tub is physically on the trolley, and refusing to record it would put
    // the ledger at odds with the shelf. Flagged amber instead.
    scannedByInk.forEach((units, inkKey) => {
      if (seen.has(inkKey)) return;
      const product = productByKey.get(inkKey) ?? null;
      out.push({
        inkKey,
        product,
        name: product?.description ?? inkKey,
        hex: product?.hex_colour ?? null,
        unit: null,
        demandKg: 0,
        targetTubs: null,
        tubKg: hints.get(inkKey)?.nominalKg ?? null,
        fifo: hints.get(inkKey),
        scanned: units,
      });
    });

    // Sticker order when we have it (matches the Job Bag), heaviest first when
    // we don't. Extras (no demand) always sink to the bottom.
    return out.sort((a, b) => {
      if ((a.demandKg > 0) !== (b.demandKg > 0)) return a.demandKg > 0 ? -1 : 1;
      if (a.unit != null && b.unit != null) return a.unit - b.unit;
      if (a.unit != null) return -1;
      if (b.unit != null) return 1;
      return b.demandKg - a.demandKg;
    });
  }, [demand, scannedCodes, unitByCode, productByKey, hints, unitByInk]);

  const totals = useMemo(() => {
    const tubs = scannedCodes.length;
    const kg = scannedCodes.reduce((s, c) => s + Number(unitByCode.get(c)?.nominal_kg ?? 0), 0);
    const wanted = rows.filter((r) => r.demandKg > 0);
    const done = wanted.filter((r) => r.targetTubs != null && r.scanned.length >= r.targetTubs).length;
    return { tubs, kg, done, wanted: wanted.length };
  }, [scannedCodes, unitByCode, rows]);

  // ---- scanning ------------------------------------------------------------
  const focusScan = useCallback(() => scanRef.current?.focus(), []);

  const submitScan = useCallback((raw: string) => {
    const outcome = resolveScan(raw, unitByCode, scannedSet);
    setScanInput('');
    if (outcome.kind === 'unknown') {
      setFeedback({
        tone: 'bad',
        title: `${raw.trim().toUpperCase()} — not a sealed tub in the store`,
        detail: 'Already kitted, already opened, or not a tub we hold. Check the label.',
      });
      return;
    }
    if (outcome.kind === 'ambiguous') {
      setFeedback({
        tone: 'bad',
        title: `"${raw.trim().toUpperCase()}" matches ${outcome.candidates.length} tubs`,
        detail: `Scan the full code — ${outcome.candidates.join(', ')}`,
      });
      return;
    }
    if (outcome.kind === 'duplicate') {
      setFeedback({
        tone: 'warn',
        title: `${outcome.unit.unit_code} is already on this trolley`,
        detail: 'Each tub goes on once.',
      });
      return;
    }

    const u = outcome.unit;
    const row = rows.find((r) => r.inkKey === u.ink_key);
    const hint = hints.get(u.ink_key);
    const next = (row?.scanned.length ?? 0) + 1;
    setScannedCodes((c) => [...c, u.unit_code]);

    // FIFO is a warning, never a block: the tub is already in the picker's hand
    // and the shelf is the truth. The backend warns on the same rule at kit.
    if (hint && u.batch_no > hint.batchNo) {
      setFeedback({
        tone: 'warn',
        title: `${u.unit_code} added — but this isn't the oldest batch`,
        detail: `That tub is batch ${u.batch_no}; batch ${hint.batchNo} is older and still has ${hint.sealedTubs} tub${hint.sealedTubs === 1 ? '' : 's'} in the store. Take the oldest first.`,
      });
      return;
    }
    if (!row || row.demandKg === 0) {
      setFeedback({
        tone: 'warn',
        title: `${u.unit_code} added — ${u.ink_key} isn't in this run's demand`,
        detail: 'Recorded anyway so the trolley matches the shelf, but check it belongs on this run.',
      });
      return;
    }
    setFeedback({
      tone: 'ok',
      title: `${u.ink_key} · tub ${next}${row.targetTubs != null ? ` of ${row.targetTubs}` : ''}`,
      detail: `${u.unit_code} · batch ${u.batch_no}`,
    });
  }, [unitByCode, scannedSet, rows, hints]);

  const removeScan = (code: string) => {
    setScannedCodes((c) => c.filter((x) => x !== code));
    setFeedback(null);
    focusScan();
  };

  // The scan box is the only input on the pick step, so it keeps focus: a wedge
  // scanner types wherever the caret is, and a lost caret silently eats a scan.
  useEffect(() => {
    if (!editingSetup && !result) focusScan();
  }, [editingSetup, result, scannedCodes.length, focusScan]);

  // ---- setup handlers ------------------------------------------------------
  const pickGroup = (g: JobGroup | null) => {
    setGroup(g);
    setJobs(g ? g.job_numbers : []);
    if (g?.press_code) setPressCode(up(g.press_code));
    setScannedCodes([]);
    setFeedback(null);
  };

  const addJob = () => {
    const raw = jobDraft.split(',').map((s) => up(s.trim())).filter(Boolean);
    if (raw.length === 0) return;
    setJobs((cur) => Array.from(new Set([...cur, ...raw])));
    setJobDraft('');
  };

  const toggleManual = () => {
    setManualJobs((cur) => !cur);
    setGroup(null);
    setJobs([]);
    setJobDraft('');
    setScannedCodes([]);
  };

  const chosenTrolley = trolleys.find((t) => t.location_code === trolleyCode);
  const trolleyBusy = chosenTrolley?.open_run_id != null;
  const setupReady = !!trolleyCode && !trolleyBusy && activeJobs.length > 0;

  const kit = useMutation({
    mutationFn: (payload: InkTrolleyKitPayload) => kitInkTrolley(payload),
    onSuccess: (res) => { setResult({ run: res.run, warnings: res.warnings }); invalidate(res.run.id); },
  });

  const submitKit = () => {
    const byInk = new Map<string, string[]>();
    scannedCodes.forEach((c) => {
      const u = unitByCode.get(c);
      if (!u) return;
      const a = byInk.get(u.ink_key) ?? [];
      a.push(c);
      byInk.set(u.ink_key, a);
    });
    kit.mutate({
      trolley_code: trolleyCode,
      job_numbers: activeJobs,
      family_run_id: manualJobs ? null : (group?.family_run_id ?? null),
      press_code: pressCode || null,
      items: Array.from(byInk.entries()).map(([ink_key, unit_codes]) => ({ ink_key, unit_codes })),
    });
  };

  // =========================================================================
  // DONE
  // =========================================================================
  if (result) {
    return (
      <Box sx={{ maxWidth: 720, mx: 'auto', p: { xs: 2, md: 3 }, textAlign: 'center' }}>
        <CheckCircleRounded sx={{ fontSize: 72, color: TOKENS.good, mt: 4 }} />
        <Typography sx={{ fontSize: 26, fontWeight: 800, mt: 1 }}>
          {result.run.trolley_code} is kitted
        </Typography>
        <Typography sx={{ fontSize: 14, color: TOKENS.ink2, mt: 0.5 }}>
          Run #{result.run.id} · {totals.tubs} tub{totals.tubs === 1 ? '' : 's'} · {fmtKg(totals.kg)}
          {pressCode ? ` · ${pressCode}` : ''}
        </Typography>
        <Alert severity="info" sx={{ mt: 3, textAlign: 'left', fontSize: 13 }}>
          The ink has moved out of the store and onto the trolley — nothing is consumed yet.
          Usage is booked when the trolley comes back and you reconcile it.
        </Alert>
        {result.warnings.length > 0 && (
          <Alert severity="warning" sx={{ mt: 1.5, textAlign: 'left', fontSize: 13 }}>
            {result.warnings.map((w) => <div key={w}>{w}</div>)}
          </Alert>
        )}
        <Stack direction="row" spacing={1.5} justifyContent="center" sx={{ mt: 3 }}>
          <Button size="large" variant="outlined" onClick={() => navigate('/production/ink-trolley')}>
            Back to trolleys
          </Button>
          <Button size="large" variant="contained"
            onClick={() => {
              setResult(null); setGroup(null); setJobs([]); setTrolleyCode('');
              setScannedCodes([]); setFeedback(null); setEditingSetup(true);
            }}
            sx={{ bgcolor: TOKENS.accent, '&:hover': { bgcolor: TOKENS.accentStrong } }}>
            Kit another
          </Button>
        </Stack>
      </Box>
    );
  }

  // =========================================================================
  // SETUP + PICK
  // =========================================================================
  return (
    <Box sx={{ maxWidth: 760, mx: 'auto', pb: 14 }}>
      {/* ---------- header ---------- */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ px: { xs: 1.5, md: 0 }, pt: 2, pb: 1 }}>
        <IconButton onClick={() => navigate('/production/ink-trolley')} size="small">
          <ArrowBackRounded />
        </IconButton>
        <Typography sx={{ fontWeight: 800, fontSize: 20 }}>Kit a trolley</Typography>
      </Stack>

      {/* ---------- setup ---------- */}
      {editingSetup ? (
        <Box sx={{ px: { xs: 1.5, md: 0 } }}>
          <SetupCard
            title="1 · What are you kitting?"
            help={manualJobs
              ? 'No demand is computed for a hand-typed run — you pick from the shelf on your own judgement.'
              : 'Groups come straight off the Planning Board, earliest due first. One forme = one gang run.'}
          >
            {!manualJobs ? (
              <Autocomplete<JobGroup>
                options={groupsQ.data ?? []}
                loading={groupsQ.isLoading}
                value={group}
                onChange={(_e, v) => pickGroup(v)}
                filterOptions={(opts, state) => {
                  const q = state.inputValue.trim().toLowerCase();
                  if (!q) return opts;
                  return opts.filter((o) => (
                    `${o.customer_code} ${o.customer_name} ${jobGroupLabel(o)} ${o.press_code ?? 'backlog'} ${o.job_numbers.join(' ')}`
                      .toLowerCase().includes(q)
                  ));
                }}
                getOptionLabel={(o) => `${o.customer_code} · ${jobGroupLabel(o)}`}
                isOptionEqualToValue={(o, v) => o.group_key === v.group_key}
                renderOption={(props, o) => (
                  <li {...props} key={o.group_key}>
                    <Box sx={{ minWidth: 0, flex: 1, py: 0.25 }}>
                      <Stack direction="row" alignItems="baseline" spacing={0.75}>
                        <Typography sx={{ fontWeight: 800, fontSize: 14 }}>{o.customer_code}</Typography>
                        <Typography sx={{ fontFamily: TOKENS.monoFont, fontSize: 13, color: TOKENS.ink2 }}>
                          {jobGroupLabel(o)}
                        </Typography>
                        {o.scheduled_count > 0 && !o.fully_scheduled && (
                          <Chip size="small" label="PART-SCHEDULED"
                            sx={{ height: 17, fontSize: 9.5, fontWeight: 800, color: '#92400e', bgcolor: '#f59e0b26' }} />
                        )}
                      </Stack>
                      <Typography sx={{ fontSize: 12, color: TOKENS.ink3 }}>
                        {o.job_count} job{o.job_count === 1 ? '' : 's'} · due {fmtDate(o.earliest_due)}
                        {' · '}{o.total_sheets.toLocaleString()} sheets
                      </Typography>
                    </Box>
                    <Chip size="small" label={o.press_code ?? 'BACKLOG'}
                      sx={{
                        ml: 1, height: 19, fontSize: 10.5, fontWeight: 800, fontFamily: TOKENS.monoFont,
                        color: o.press_code ? TOKENS.accentStrong : TOKENS.ink3,
                        bgcolor: o.press_code ? `${TOKENS.accent}1F` : TOKENS.lineSoft,
                      }} />
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} label="Gang run" placeholder="Customer, forme or job number"
                    error={groupsQ.isError}
                    helperText={groupsQ.isError ? `Couldn’t load job groups: ${errMsg(groupsQ.error)}` : undefined} />
                )}
              />
            ) : (
              <>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: jobs.length ? 1.25 : 0 }}>
                  {jobs.map((j) => (
                    <Chip key={j} label={j} onDelete={() => setJobs((c) => c.filter((x) => x !== j))}
                      sx={{ fontFamily: TOKENS.monoFont, fontWeight: 700 }} />
                  ))}
                </Stack>
                <TextField fullWidth label="Add job number" value={jobDraft}
                  onChange={(e) => setJobDraft(up(e.target.value))}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addJob(); } }}
                  onBlur={addJob} />
              </>
            )}
            <Button size="small" onClick={toggleManual} sx={{ mt: 1, color: TOKENS.ink3, fontSize: 12.5 }}>
              {manualJobs ? 'Pick a board group instead' : 'Type job numbers instead'}
            </Button>
          </SetupCard>

          <SetupCard title="2 · Which trolley?">
            {trolleys.length === 0 ? (
              <Alert severity="warning" sx={{ fontSize: 13 }}>No trolleys are set up.</Alert>
            ) : (
              <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)' } }}>
                {trolleys.map((t) => (
                  <TrolleyTile key={t.location_code} trolley={t}
                    selected={t.location_code === trolleyCode}
                    onSelect={() => setTrolleyCode(t.location_code)} />
                ))}
              </Box>
            )}
          </SetupCard>

          <SetupCard title="3 · Which press?"
            help="Recorded on the run. It doesn’t limit which gang runs you can kit — you often kit for something not yet on a lane.">
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Press</InputLabel>
              <Select label="Press" value={pressCode}
                onChange={(e) => setPressCode(up(String(e.target.value)))}>
                <MenuItem value=""><em>Not set</em></MenuItem>
                {pressCodes.map((code) => (
                  <MenuItem key={code} value={code}>
                    <span style={{ fontWeight: 700, fontFamily: TOKENS.monoFont }}>{code}</span>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </SetupCard>

          <Button fullWidth size="large" variant="contained" disabled={!setupReady}
            onClick={() => { setEditingSetup(false); setTimeout(focusScan, 50); }}
            startIcon={<QrCodeScannerRounded />}
            sx={{ mt: 1, py: 1.5, fontSize: 16, fontWeight: 700, borderRadius: '12px',
              bgcolor: TOKENS.accent, '&:hover': { bgcolor: TOKENS.accentStrong } }}>
            Start picking
          </Button>
        </Box>
      ) : (
        <>
          {/* ---------- sticky run header + scan bar ---------- */}
          <Box sx={{
            position: 'sticky', top: 0, zIndex: 3, bgcolor: TOKENS.bg,
            px: { xs: 1.5, md: 0 }, pb: 1.25, borderBottom: `1px solid ${TOKENS.line}`,
          }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 800, fontSize: 15, lineHeight: 1.3 }}>
                  {group ? `${group.customer_code} · ${jobGroupLabel(group)}` : `${jobs.length} job${jobs.length === 1 ? '' : 's'}`}
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: TOKENS.ink3 }}>
                  <b style={{ fontFamily: TOKENS.monoFont, color: TOKENS.ink2 }}>{trolleyCode}</b>
                  {pressCode ? ` → ${pressCode}` : ''}
                  {' · '}{activeJobs.join(', ')}
                </Typography>
              </Box>
              <Button size="small" startIcon={<EditRounded />} onClick={() => setEditingSetup(true)}
                sx={{ color: TOKENS.ink3, flexShrink: 0 }}>Change</Button>
            </Stack>

            <TextField
              inputRef={scanRef}
              fullWidth autoFocus value={scanInput}
              onChange={(e) => setScanInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitScan(scanInput); } }}
              placeholder="Scan a tub"
              InputProps={{
                startAdornment: <QrCodeScannerRounded sx={{ mr: 1, color: TOKENS.ink3 }} />,
                sx: { fontFamily: TOKENS.monoFont, fontSize: 18, fontWeight: 700, bgcolor: TOKENS.panel, borderRadius: '12px' },
              }}
            />
            <Collapse in={feedback != null}>
              {feedback && <ScanFeedback feedback={feedback} />}
            </Collapse>
          </Box>

          <Box sx={{ px: { xs: 1.5, md: 0 }, pt: 1.5 }} onClick={focusScan}>
            {demandQ.isLoading && (
              <Stack direction="row" alignItems="center" spacing={1.25} sx={{ color: TOKENS.ink3, py: 3 }}>
                <CircularProgress size={18} />
                <Typography sx={{ fontSize: 13.5 }}>Working out what this run needs…</Typography>
              </Stack>
            )}
            {demandQ.isError && (
              <Alert severity="error" sx={{ fontSize: 13 }}>
                Couldn’t work out the ink demand: {errMsg(demandQ.error)}
              </Alert>
            )}

            {kit.isError && <Alert severity="error" sx={{ mb: 1.5 }}>{errMsg(kit.error)}</Alert>}

            <Stack spacing={1.25}>
              {rows.map((r) => (
                <PickTile key={r.inkKey} row={r} onRemove={removeScan} />
              ))}
            </Stack>

            {/* Separations the artwork needs but nobody has chosen an ink for.
                You can't draw "Cyan" off a shelf, only a real product. */}
            {unassigned.length > 0 && (
              <Alert severity="error" sx={{ mt: 1.5, fontSize: 13 }}
                action={(
                  <Button size="small" color="inherit" startIcon={<PaletteRounded />}
                    onClick={() => navigate('/production/ink-assign')}>Assign</Button>
                )}>
                <AlertTitle sx={{ fontSize: 13.5, fontWeight: 800, mb: 0.25 }}>
                  {unassigned.length} separation{unassigned.length === 1 ? '' : 's'} have no ink chosen
                </AlertTitle>
                {unassigned.map((r) => `${r.name} (${fmtKg(r.total_kg)})`).join(' · ')} — the artwork needs
                these and we know the weight, but not WHICH product covers them. They can’t be picked.
              </Alert>
            )}

            {demand && demand.pending_extraction.length > 0 && (
              <Alert severity="info" sx={{ mt: 1.5, fontSize: 13 }}>
                {demand.pending_extraction.length} job{demand.pending_extraction.length === 1 ? '' : 's'} have
                no cached artwork ({demand.pending_extraction.map((j) => j.job_number).join(', ')}) — the real
                demand is higher than shown.
              </Alert>
            )}

            {rows.length === 0 && !demandQ.isLoading && activeJobs.length > 0 && (
              <Box sx={{ border: `1px dashed ${TOKENS.line}`, borderRadius: '12px', p: 4, textAlign: 'center', color: TOKENS.ink3 }}>
                <Typography sx={{ fontSize: 13.5 }}>
                  Nothing to pick from demand — scan tubs and they’ll appear here.
                </Typography>
              </Box>
            )}
          </Box>

          {/* ---------- sticky footer ---------- */}
          <Box sx={{
            position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 4,
            bgcolor: TOKENS.panel, borderTop: `1px solid ${TOKENS.line}`,
            boxShadow: '0 -4px 16px rgba(35,28,20,.06)', px: 2, py: 1.25,
          }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}
              sx={{ maxWidth: 760, mx: 'auto' }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 17, fontWeight: 800, fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}>
                  {totals.tubs} tub{totals.tubs === 1 ? '' : 's'} · {fmtKg(totals.kg)}
                </Typography>
                <Typography sx={{ fontSize: 12, color: totals.wanted > 0 && totals.done === totals.wanted ? TOKENS.good : TOKENS.ink3, fontWeight: 700 }}>
                  {totals.done} of {totals.wanted} ink{totals.wanted === 1 ? '' : 's'} complete
                </Typography>
              </Box>
              <Button size="large" variant="contained"
                disabled={totals.tubs === 0 || kit.isPending}
                startIcon={kit.isPending ? <CircularProgress size={18} color="inherit" /> : <LocalShippingRounded />}
                onClick={submitKit}
                sx={{ px: 3, py: 1.25, fontSize: 15, fontWeight: 800, borderRadius: '12px', flexShrink: 0,
                  bgcolor: TOKENS.accent, '&:hover': { bgcolor: TOKENS.accentStrong } }}>
                Kit trolley
              </Button>
            </Stack>
          </Box>
        </>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------

function SetupCard({ title, help, children }: {
  title: string; help?: string; children: React.ReactNode;
}) {
  return (
    <Box sx={{
      bgcolor: TOKENS.panel, border: `1px solid ${TOKENS.line}`, borderRadius: '14px',
      p: { xs: 1.75, md: 2 }, mb: 1.5, boxShadow: TOKENS.shadowSm,
    }}>
      <Typography sx={{ fontSize: 11.5, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase', color: TOKENS.ink3, mb: 1.25 }}>
        {title}
      </Typography>
      {children}
      {help && (
        <Typography sx={{ fontSize: 12, color: TOKENS.ink3, mt: 1, lineHeight: 1.5 }}>{help}</Typography>
      )}
    </Box>
  );
}

function TrolleyTile({ trolley, selected, onSelect }: {
  trolley: InkLocation; selected: boolean; onSelect: () => void;
}) {
  const busy = trolley.open_run_id != null;
  return (
    <Box
      onClick={busy ? undefined : onSelect}
      sx={{
        p: 1.5, borderRadius: '12px', textAlign: 'center', userSelect: 'none',
        cursor: busy ? 'not-allowed' : 'pointer',
        border: `2px solid ${selected ? TOKENS.accent : TOKENS.line}`,
        bgcolor: busy ? TOKENS.lineSoft : selected ? `${TOKENS.accent}12` : TOKENS.panel,
        opacity: busy ? 0.55 : 1,
        transition: 'border-color .12s, background-color .12s',
      }}
    >
      <Typography sx={{ fontFamily: TOKENS.monoFont, fontWeight: 800, fontSize: 15 }}>
        {trolley.location_code}
      </Typography>
      <Typography sx={{ fontSize: 11.5, color: busy ? TOKENS.warn : TOKENS.good, fontWeight: 700 }}>
        {busy ? `run #${trolley.open_run_id}` : 'free'}
      </Typography>
    </Box>
  );
}

function ScanFeedback({ feedback }: { feedback: NonNullable<Feedback> }) {
  const style = {
    ok:   { fg: TOKENS.good, bg: `${TOKENS.good}14`, Icon: CheckCircleRounded },
    warn: { fg: '#92400e',   bg: `${TOKENS.warn}1F`, Icon: WarningAmberRounded },
    bad:  { fg: TOKENS.bad,  bg: `${TOKENS.bad}14`,  Icon: ErrorOutlineRounded },
  }[feedback.tone];
  const { Icon } = style;
  return (
    <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{
      mt: 1, p: 1.25, borderRadius: '10px', bgcolor: style.bg, border: `1px solid ${style.fg}33`,
    }}>
      <Icon sx={{ color: style.fg, fontSize: 22, mt: '-1px' }} />
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: style.fg, lineHeight: 1.35 }}>
          {feedback.title}
        </Typography>
        {feedback.detail && (
          <Typography sx={{ fontSize: 12, color: TOKENS.ink2, lineHeight: 1.45 }}>{feedback.detail}</Typography>
        )}
      </Box>
    </Stack>
  );
}

/**
 * One ink on the pick list.
 *
 * The whole tile changes colour as it fills: neutral untouched, accent while
 * part-picked, GREEN with a tick the moment the count is met. That green is the
 * only thing a picker walking the shelves needs to read from arm's length.
 */
function PickTile({ row, onRemove }: { row: PickRow; onRemove: (code: string) => void }) {
  const got = row.scanned.length;
  const target = row.targetTubs;
  const extra = row.demandKg === 0;
  const complete = target != null && got >= target;
  const over = target != null && got > target;

  const state = extra ? 'extra' : over ? 'over' : complete ? 'done' : got > 0 ? 'part' : 'todo';
  const accent = {
    todo:  TOKENS.line,
    part:  TOKENS.accent,
    done:  TOKENS.good,
    over:  TOKENS.warn,
    extra: TOKENS.warn,
  }[state];
  const tint = {
    todo:  TOKENS.panel,
    part:  TOKENS.panel,
    done:  `${TOKENS.good}0F`,
    over:  `${TOKENS.warn}12`,
    extra: `${TOKENS.warn}0F`,
  }[state];

  return (
    <Box sx={{
      border: `1px solid ${accent}`, borderLeft: `6px solid ${accent}`, borderRadius: '14px',
      bgcolor: tint, p: 1.5, boxShadow: TOKENS.shadowSm,
    }}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box sx={{
          width: 44, height: 44, borderRadius: '10px', flexShrink: 0,
          bgcolor: row.hex ?? '#ccc', border: '1px solid rgba(0,0,0,0.18)',
        }} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" alignItems="baseline" spacing={0.75} flexWrap="wrap" useFlexGap>
            {row.unit != null && (
              <Chip size="small" label={`U${row.unit}`}
                sx={{ height: 19, fontSize: 10.5, fontWeight: 800, fontFamily: TOKENS.monoFont,
                  color: TOKENS.ink2, bgcolor: TOKENS.lineSoft }} />
            )}
            <Typography sx={{ fontFamily: TOKENS.monoFont, fontWeight: 800, fontSize: 15 }}>
              {row.inkKey}
            </Typography>
          </Stack>
          <Typography sx={{ fontSize: 12.5, color: TOKENS.ink2, lineHeight: 1.4 }}>{row.name}</Typography>
        </Box>

        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
          {complete && !over ? (
            <CheckCircleRounded sx={{ color: TOKENS.good, fontSize: 34 }} />
          ) : (
            <>
              <Typography sx={{ fontSize: 20, fontWeight: 800, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1, color: accent === TOKENS.line ? TOKENS.ink : accent }}>
                {got}{target != null ? ` / ${target}` : ''}
              </Typography>
              <Typography sx={{ fontSize: 10.5, color: TOKENS.ink3, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                tubs
              </Typography>
            </>
          )}
        </Box>
      </Stack>

      {/* What the run needs, and which batch to take for it. */}
      <Stack direction="row" spacing={1.5} sx={{ mt: 1, flexWrap: 'wrap' }} useFlexGap>
        {!extra && (
          <Typography sx={{ fontSize: 12, color: TOKENS.ink2 }}>
            needs <b>{fmtKg(row.demandKg)}</b>
            {target != null && row.tubKg ? ` · ${target} × ${row.tubKg} kg tub${target === 1 ? '' : 's'}` : ''}
          </Typography>
        )}
        {row.fifo && (
          <Typography sx={{ fontSize: 12, color: TOKENS.ink3 }}>
            take batch <b style={{ fontFamily: TOKENS.monoFont, color: TOKENS.ink2 }}>{row.fifo.batchNo}</b>
            {row.fifo.batchCount > 1 ? ' (oldest)' : ''} · {row.fifo.sealedTubs} in store
          </Typography>
        )}
        {!row.fifo && !extra && (
          <Typography sx={{ fontSize: 12, color: TOKENS.bad, fontWeight: 700 }}>
            none sealed in the store
          </Typography>
        )}
      </Stack>

      {target != null && target > 0 && !extra && (
        <LinearProgress variant="determinate" value={Math.min(100, (got / target) * 100)}
          sx={{
            mt: 1, height: 6, borderRadius: 3, bgcolor: TOKENS.lineSoft,
            '& .MuiLinearProgress-bar': { bgcolor: accent === TOKENS.line ? TOKENS.accent : accent, borderRadius: 3 },
          }} />
      )}

      {extra && (
        <Typography sx={{ fontSize: 12, color: '#92400e', fontWeight: 700, mt: 0.75 }}>
          Not in this run’s demand — recorded so the trolley matches the shelf.
        </Typography>
      )}
      {over && (
        <Typography sx={{ fontSize: 12, color: '#92400e', fontWeight: 700, mt: 0.75 }}>
          {got - target!} more than the run needs.
        </Typography>
      )}

      {row.scanned.length > 0 && (
        <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.5} sx={{ mt: 1 }}>
          {row.scanned.map((u) => (
            <Chip key={u.unit_code} size="small" label={u.unit_code}
              onDelete={() => onRemove(u.unit_code)} deleteIcon={<CloseRounded />}
              sx={{ height: 24, fontSize: 11, fontFamily: TOKENS.monoFont, fontWeight: 700,
                bgcolor: TOKENS.panel, border: `1px solid ${TOKENS.line}` }} />
          ))}
        </Stack>
      )}
    </Box>
  );
}
